// Live hydration for the at-a-glance layouts.
//
// Both layouts store an EMMA code and the author's overrides, nothing about
// the hotel itself. A block parser has to return a string synchronously, so it
// returns the cover - which needs only stored fields - and registers itself
// here; the body is filled in when the hotel lands.
//
// Shared rather than per-layout so a deck mixing both runs one observer and
// exposes one readiness signal.

import {
  fetchHotelDetail,
  pendingHotelDetails,
} from "../../../../rhgHotel/hotelDetailCache";

const PENDING_ATTR = "data-aag-pending";
const ID_ATTR = "data-aag-id";

let seq = 0;
const waiting = new Map();
let observer = null;

/**
 * Registers a block whose body is still to come.
 *
 * @param {object} entry
 *   - code: EMMA code to load.
 *   - parts: hotel-detail properties this layout reads.
 *   - render: (api) => string, the body markup for a payload.
 * @returns {string} the id to put in the container's data-aag-id.
 */
export function registerPending(entry) {
  const id = `aag-${++seq}`;
  waiting.set(id, entry);
  watch();
  // The observer catches containers inserted after this returns; this covers
  // one that is already in the DOM by the time the parser runs.
  if (typeof requestAnimationFrame === "function") {
    requestAnimationFrame(() => sweep(document));
  }
  return id;
}

export const pendingAttrs = (id) => `${ID_ATTR}="${id}" ${PENDING_ATTR}="1"`;

function fill(el, entry, api) {
  el.innerHTML = entry.render(api);
  el.removeAttribute(PENDING_ATTR);
  // Reveal swaps data-src -> src only for slides it has already walked, and
  // this markup arrives after that pass, so images on the current slide would
  // otherwise sit unloaded. Same for the location-map frames: reveal loads a
  // slide's iframes when it becomes current, which for the slide the deck
  // opened on has already happened - without this the map never loads.
  if (el.closest(".present")) {
    el.querySelectorAll(
      "img[data-src], .map-section iframe[data-src], .aag-me-map-wrap iframe[data-src]",
    ).forEach((media) => {
      media.src = media.getAttribute("data-src");
      media.removeAttribute("data-src");
    });
  }
}

function hydrate(el) {
  const id = el.getAttribute(ID_ATTR);
  const entry = waiting.get(id);
  if (!entry) return;
  waiting.delete(id);

  if (!entry.code) {
    fill(el, entry, {});
    return;
  }

  fetchHotelDetail(entry.code, entry.parts)
    .then((api) => fill(el, entry, api))
    .catch(() => {
      // An empty payload renders the same slide with everything the hotel
      // would have supplied reported as absent, which is the honest result and
      // keeps the cover, the overrides and the navigation intact.
      fill(el, entry, {});
      el.setAttribute("data-aag-error", "1");
    });
}

function sweep(root) {
  (root || document).querySelectorAll?.(`[${PENDING_ATTR}]`)?.forEach(hydrate);
}

// One observer for the deck: reveal builds slides as it goes, so blocks appear
// long after the first parse.
function watch() {
  if (observer || typeof MutationObserver === "undefined") return;
  observer = new MutationObserver((records) => {
    for (const r of records) {
      for (const node of r.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.hasAttribute?.(PENDING_ATTR)) hydrate(node);
        else sweep(node);
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

// Resolves once nothing is waiting and no fetch is running, so a capture can
// photograph a real slide rather than a cover. Settled, not successful: a
// hotel that fails to load still renders, and a capture must not hang on it.
const settled = () =>
  !document.querySelector(`[${PENDING_ATTR}]`) &&
  pendingHotelDetails().length === 0;

window.aagReady = function (timeoutMs = 20000) {
  if (settled()) return Promise.resolve(true);

  return new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      if (settled()) return resolve(true);
      // A wedged origin must not hold a thumbnail run open indefinitely; the
      // slide still renders, just without that hotel.
      if (Date.now() - started > timeoutMs) return resolve(false);
      setTimeout(tick, 50);
    };
    tick();
  });
};
